<template>
  <q-card
    flat
    bordered
    :class="hasError ? 'border-negative' : ''"
    class="row justify-between items-center q-pa-xs no-wrap"
  >
    <div class="row items-center no-wrap">
      <q-avatar
        size="md"
        :color="`${color}-light`"
        :text-color="color"
        icon="mdi-file-document-outline"
      />
      <div class="column q-px-sm re-lg">
        <a
          :href="documentURL"
          :title="props.fileName"
          target="_blank"
          class="text-black"
        >
          {{ formattedFileName }}
        </a>
        <span
          v-if="caption.message"
          class="text-caption"
          :class="caption.colorClass"
        >
          {{ caption.message }}
        </span>
      </div>
    </div>

    <div
      v-if="isLoading || isDeleting"
      class="spinner-wrapper q-pa-sm"
    >
      <q-spinner
        color="secondary"
        size="xs"
      />
    </div>
    <re-button
      v-else-if="!readonly"
      icon="mdi-delete-outline"
      text-color="secondary"
      rounded
      flat
      @click="showDeleteDialog = true"
    />
  </q-card>

  <re-dialog
    v-model="showDeleteDialog"
    :title="t('valuer_tool.documents.delete-modal.title')"
    :confirm-label="t('monolith.global.delete')"
    has-cancel
    has-confirm
    has-close-button
    header-icon="mdi-exclamation"
    :type="enums.feedbackType.NEGATIVE"
    @confirm="handleConfirmDelete"
  >
    <re-banner color="negative">
      {{ t('valuer_tool.documents.delete-modal.banner') }}
    </re-banner>
  </re-dialog>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import enums from '@/plugins/enums'
import utils from '@/plugins/utils'

const { t } = useI18n()

const props = defineProps({
  fileName: {
    type: String,
    default: null,
  },
  file: {
    type: File,
    default: null,
  },
  requestRef: {
    type: String,
    default: null,
  },
  documentRef: {
    type: String,
    default: null,
  },
  readonly: {
    type: Boolean,
    default: null,
  },
  purpose: {
    type: String,
    default: null,
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isDeleting: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['delete'])

const showDeleteDialog = ref(false)

const handleConfirmDelete = () => {
  emit('delete', props.documentRef || props.file)
}

const documentURL = computed(() => {
  return props.file
    ? URL.createObjectURL(props.file)
    : utils.getDocumentUrl(props.requestRef, props.documentRef, 'view=true')
})

const fileName = computed(() => props.file?.name || props.fileName)
const color = computed(() => (props.hasError ? 'negative' : 'primary'))

const formattedFileName = computed(() => {
  const MAX_LENGTH = 46
  if (fileName.value.length > MAX_LENGTH) {
    return `${fileName.value.slice(0, MAX_LENGTH / 2)}…${fileName.value.slice(-MAX_LENGTH / 2)}`
  }
  return fileName.value
})

const caption = computed(() => {
  if (props.isLoading) {
    return {
      message: t('valuer_tool.documents.uploading'),
      colorClass: 'text-secondary',
    }
  }
  if (props.isDeleting) {
    return {
      message: t('valuer_tool.documents.isDeleting'),
      colorClass: 'text-secondary',
    }
  }
  if (props.hasError) {
    return {
      message: t('valuer_tool.documents.upload-error'),
      colorClass: 'text-negative',
    }
  }
  if (props.purpose) {
    return {
      message: t(`document.${props.purpose}`),
      colorClass: '',
    }
  }
  return { message: null, colorClass: '' }
})
</script>

<style scoped lang="scss">
.border-negative {
  border-color: var(--q-negative) !important;
}

.spinner-wrapper {
  background-color: var(--secondary-bground);
  border-radius: 50% !important;
}
</style>
